import ApiController from '@/services/ApiController'
import { useCustomerStore } from '@/stores/customer'
import { PaymentMethodData } from '@/types/payment'
import { defineStore, storeToRefs } from 'pinia'
import { toRaw } from 'vue'
import { useAddressesStore } from './addresses'

interface PaymentStoreState {
  paymentMethods: any[] | null
  availablePaymentMethods?: any[] | null
}

interface ThreeDSecureCheckData {
  shippingAddressId?: number | null
  shippingAddressData?: object | null
  billingAddressId?: number | null
  billingAddressData?: object | null
  paymentMethodId?: number | null
  paymentMethodData?: PaymentMethodData | null
  email: string | null
  amount: number
  isGuestCheckout?: boolean
}

export const usePaymentStore = defineStore('payment', {
  state: (): PaymentStoreState => ({
    paymentMethods: null,
    availablePaymentMethods: null
  }),
  getters: {
    hasPaymentMethods: ({ paymentMethods }): boolean =>
      !!paymentMethods && paymentMethods.length > 0,
    getPaymentMethodById:
      ({ paymentMethods }) =>
      (id: number): PaymentMethodData | null => {
        return paymentMethods
          ? (paymentMethods.find(
              (method) => method.customer_payment_method_id === id
            ) ?? null)
          : null
      },
    getDefaultPaymentMethod: ({ paymentMethods }) => {
      if (!paymentMethods || paymentMethods.length === 0) return null
      const defaultPaymentMethod = paymentMethods.find(
        (a) => a.is_default_payment_method
      )
      return defaultPaymentMethod ?? paymentMethods[0]
    },
    isPaymentMethodAvailable: ({ availablePaymentMethods }) => {
      return (methodCode: string): boolean => {
        if (!availablePaymentMethods) return false
        return availablePaymentMethods.includes(methodCode)
      }
    }
  },
  actions: {
    /**
     * @returns whether the methods were loaded. The checkout callers ignore it and keep
     *   their existing behaviour of carrying on with whatever is in the store; the account
     *   tab needs it to tell "no saved methods" apart from "the read failed", which used
     *   to look identical here (VIP-12081).
     */
    async fetch(): Promise<boolean> {
      const customerStore = useCustomerStore()
      const { customerActive } = storeToRefs(customerStore)
      await customerStore.waitUntilPending()

      try {
        let paymentMethodsPromise = Promise.resolve([])
        if (customerActive.value) {
          paymentMethodsPromise = ApiController.fetchCustomerPaymentMethods()
        }
        const [available, paymentMethods] = await Promise.all([
          ApiController.fetchAvailablePaymentMethods(),
          paymentMethodsPromise
        ])

        this.availablePaymentMethods = _sortPaymentMethods(available)
        // For guest users, set to empty array instead of null so loading state clears
        this.paymentMethods = paymentMethods || []
        return true
      } catch (e) {
        console.error('Error fetching payment methods:', e)
        // On error, set to empty array for guest users
        if (!customerActive.value) {
          this.paymentMethods = []
        }
        return false
      }
    },

    /**
     * Re-reads only the customer's own saved methods after a mutation. Unlike fetch() this
     * leaves the shop-wide availablePaymentMethods list untouched: editing a saved method
     * can't change which methods the shop offers, so re-fetching it would be a wasted round
     * trip (VIP-12081). Returns whether the read succeeded so callers can tell a mutation
     * that "took but the list is now stale" apart from a clean success.
     */
    async refreshPaymentMethods(): Promise<boolean> {
      const customerStore = useCustomerStore()
      const { customerActive } = storeToRefs(customerStore)
      if (!customerActive.value) return true

      try {
        this.paymentMethods =
          (await ApiController.fetchCustomerPaymentMethods()) || []
        return true
      } catch (e) {
        console.error('Error refreshing payment methods:', e)
        return false
      }
    },

    /**
     * Removes a saved method. The API also writes the audit row the legacy account page
     * left behind, so nothing has to be logged from here.
     */
    async deletePaymentMethod(id: number): Promise<void> {
      try {
        await ApiController.deletePaymentMethod(id)
      } catch (e) {
        await ApiController.logPaymentError('unknown', e)
        throw e
      }
      // The delete succeeded; if the refresh doesn't, the just-deleted card would linger in
      // the list behind a success toast, so surface it instead of swallowing the boolean.
      if (!(await this.refreshPaymentMethods())) {
        throw new Error('Payment method deleted but the list refresh failed')
      }
    },
    async savePaymentMethod(data: PaymentMethodData): Promise<number | null> {
      // Prepayment doesn't need to be saved
      if (data.method_code === 'prepayment') {
        return null
      }

      let response
      try {
        delete data.customer_payment_method_id
        response = await ApiController.savePaymentMethod(data)
      } catch (e) {
        await ApiController.logPaymentError(data.method_code || 'unknown', e)
        throw e
      }
      if (!(await this.refreshPaymentMethods())) {
        throw new Error('Payment method saved but the list refresh failed')
      }
      return response?.data?.id ?? null
    },
    async updatePaymentMethod(
      id: number,
      data: PaymentMethodData
    ): Promise<void> {
      try {
        await ApiController.updateCustomerPaymentMethod(id, data)
      } catch (e) {
        await ApiController.logPaymentError(data.method_code || 'unknown', e)
        throw e
      }
      if (!(await this.refreshPaymentMethods())) {
        throw new Error('Payment method updated but the list refresh failed')
      }
    },
    async performThreeDSecureCheck(
      data: ThreeDSecureCheckData
    ): Promise<string | null> {
      const addressesStore = useAddressesStore()
      const paymentStore = usePaymentStore()

      let {
        shippingAddressId,
        shippingAddressData,
        billingAddressId,
        billingAddressData,
        paymentMethodId,
        paymentMethodData,
        email,
        amount
      } = data

      paymentMethodData = _resolvePaymentMethodData(
        paymentMethodData,
        paymentMethodId,
        paymentStore
      )

      if (paymentMethodData?.method_code !== 'credit_card') {
        return null
      }

      shippingAddressData = _resolveAddressData(
        shippingAddressData,
        shippingAddressId,
        addressesStore
      )

      billingAddressData = _resolveAddressData(
        billingAddressData,
        billingAddressId,
        addressesStore
      )

      if (!shippingAddressData || !billingAddressData || !paymentMethodData) {
        throw new Error('Insufficient data for 3D Secure check')
      }

      const { nonce, bin, ipAddress } = data.isGuestCheckout
        ? await _getGuestCheckout3DSData(paymentMethodData)
        : await _getAuthenticated3DSData(paymentMethodData)

      const { threeDSecureCheck } = await import('@/services/Braintree')

      try {
        const { nonce: tokenizedNonce } = await threeDSecureCheck({
          shippingAddress: shippingAddressData,
          billingAddress: billingAddressData,
          nonce,
          email,
          amount,
          bin,
          ipAddress
        })
        return tokenizedNonce
      } catch (e) {
        await ApiController.logPaymentError('credit_card', e)
        throw e
      }
    },
    cleanPaymentMethodForApi(
      paymentMethod: Record<string, any> | undefined
    ): Record<string, any> | undefined {
      if (!paymentMethod) {
        return undefined
      }

      // Remove sensitive/internal fields that shouldn't be sent to API
      const {
        bin,
        card_bin,
        tokenized_nonce,
        nonce,
        customer_payment_method_id,
        ...cleanedPaymentMethod
      } = paymentMethod

      return cleanedPaymentMethod
    }
  }
})

const _resolvePaymentMethodData = (
  paymentMethodData: any,
  paymentMethodId: number | null | undefined,
  paymentStore: any
) => {
  if (!paymentMethodData && paymentMethodId) {
    return toRaw(paymentStore.getPaymentMethodById(paymentMethodId))
  }
  return paymentMethodData
}

const _resolveAddressData = (
  addressData: any,
  addressId: number | null | undefined,
  addressesStore: any
) => {
  if (!addressData && addressId) {
    return toRaw(addressesStore.getAddressById(addressId))
  }
  return addressData
}

const _getGuestCheckout3DSData = async (paymentMethodData: any) => {
  const guestData = paymentMethodData as Record<string, any>
  const nonce = guestData.tokenized_nonce || guestData.nonce
  const bin = guestData.bin || guestData.card_bin
  const ipAddress = guestData.ip_address

  if (!nonce) throw new Error('No nonce available for 3D Secure check')
  if (!bin) throw new Error('BIN (Bank Identification Number) is required')

  return {
    nonce,
    bin,
    ipAddress: ipAddress || undefined
  }
}

const _getAuthenticated3DSData = async (paymentMethodData: any) => {
  const authData = paymentMethodData as Record<string, any>

  if (authData.token) {
    const {
      data: { braintree_method_nonce, ip_address, bin: apiBin }
    } = await ApiController.fetchBraintreeMethodNonce({
      token: authData.token
    })

    return {
      nonce: braintree_method_nonce,
      bin: apiBin,
      ipAddress: ip_address
    }
  }

  const nonce = authData.tokenized_nonce || authData.nonce
  const bin = authData.bin || authData.card_bin
  const ipAddress = authData.ip_address

  if (!nonce) throw new Error('No nonce available for 3D Secure check')
  if (!bin) throw new Error('BIN (Bank Identification Number) is required')

  return {
    nonce,
    bin,
    ipAddress: ipAddress || undefined
  }
}

const _sortPaymentMethods = (methods: string[]) => {
  const priorityMethodsOrder = ['purchase_order', 'prepayment']
  const filtered = methods.filter(
    (item: string) => !priorityMethodsOrder.includes(item)
  )

  const foundPriorityMethods = priorityMethodsOrder.filter((method: string) =>
    methods.includes(method)
  )
  if (foundPriorityMethods.length === 0) return methods

  // Return priority methods first (in order: purchase_order, then prepayment), then rest
  return [...foundPriorityMethods, ...filtered]
}
